import React, { useEffect, useState } from "react";
import { Table, Button, Spinner } from "react-bootstrap";
import Layout from "../../../layouts/default";
import { DateTime } from "luxon";
import { useFormik } from "formik";
import SmartPagination from "../EmailCampaignRunReport/DynamicSmartPagination";
import JsonModal from "../EmailCampaignRunReport/JsonModel";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { getSuppressionEmails, deleteSuppressionEmail, deleteSuppressionEmailsInBulk, deleteSuppressionEmailsByFilter } from "../../../store/slices/suppressionList/suppressionList";
import { useNavigate } from "react-router-dom";
import { hasPermission } from "../../../services/utils";
import { PERMISSIONS } from "../../../contants/permission";

const EmailSuppressionList = () => {
    const dispatch = useDispatch();
    const [checkedItems, setCheckedItems] = useState([]);
    const [page, setPage] = useState(1);
    const [showModel, setShowModel] = useState(false);
    const [selectedPayload, setSelectedPayload] = useState(null);
    const recordsPerPage = 10;
    const navigate = useNavigate();
    // match store structure
    const { suppressionEmailList } = useSelector(
        (store) => store.suppressionEmailsSlice
    ) || {};
    const currentPageData = suppressionEmailList?.records || [];
    const totalPages = suppressionEmailList?.totalPages || 0;
    const loading = suppressionEmailList?.loading;
    const [isDeleteByFilterDisabled, setIsDeleteByFilterDisabled] = useState(false);

    const formik = useFormik({
        initialValues: {
            email: "",
            type: "",
            startDate: "",
            endDate: "",
        },
        onSubmit: () => {
            setPage(1);
            fetchRecords(1);
        },
    });

    const fetchRecords = async (pageNo = page, filters = formik.values) => {
        try {
            await dispatch(
                getSuppressionEmails({
                    filterData: filters,
                    page: pageNo,
                    limit: recordsPerPage,
                })
            ).unwrap();
        } catch (error) {
            console.error("Error fetching suppression emails:", error);
            toast.error("Error fetching records");
        }
    };

    useEffect(() => {
        fetchRecords(page);
    }, [page]);

    const handleReset = () => {
        formik.resetForm();
        setPage(1);
        fetchRecords(1, { email: "", startDate: "", endDate: "" });

        // reset all selected items
        setCheckedItems([])
    };

    const showPayload = (record) => {
        setSelectedPayload(record);
        setShowModel(true);
    };

    const closeModal = () => {
        setSelectedPayload(null);
        setShowModel(false);
    };

    const handleDelete = async (id, email) => {
        if (!window.confirm("Are you sure you want to delete this email?")) return;
        try {
            await dispatch(deleteSuppressionEmail({ id, email })).unwrap();
            toast.success("Suppression email deleted successfully");
            fetchRecords(page);
        } catch (error) {
            console.error("Error deleting suppression email:", error);
            toast.error("Error deleting suppression email");
        }
    };


    const isSelectedRow = (recordId) => {
        return checkedItems.some((item) => item.id === recordId);
    };


    const handleSelect = (record) => {
        const { _id, email } = record;
        setCheckedItems((prev) => {
            const isAlreadyExist = prev.some((item) => item.id === _id);
            if (isAlreadyExist) {
                // remove that specific item
                return prev.filter((item) => item.id !== _id);
            } else {
                // add new item (keep old selections too)
                return [...prev, { id: _id, email }];
            }
        });
    };


    const isAllSelected = () => {
        if (currentPageData.length === 0) return false;
        return currentPageData.every((record) =>
            checkedItems.some((item) => item.id === record._id)
        );
    };


    const handleSelectAll = (e) => {
        if (e.target.checked) {
            // add all records from current page (avoid duplicates)
            const pageItems = currentPageData.map((record) => ({
                id: record._id,
                email: record.email,
            }));

            setCheckedItems((prev) => {
                // merge without duplicates
                const ids = new Set(prev.map((i) => i.id));
                const merged = [...prev];
                pageItems.forEach((item) => {
                    if (!ids.has(item.id)) merged.push(item);
                });
                return merged;
            });
        } else {
            // remove only current page's items
            const pageIds = new Set(currentPageData.map((record) => record._id));
            setCheckedItems((prev) => prev.filter((item) => !pageIds.has(item.id)));
        }
    };

    const handleDeleteInBulk = () => {
        if (!window.confirm(`Are you sure you want to delete ${checkedItems.length} emails in bulk`)) return;
        try {
            dispatch(deleteSuppressionEmailsInBulk(checkedItems));
            toast.success("Bulk Suppression email deleted successfully");
            fetchRecords(page);
        } catch (err) {
            console.log("Error in Deleting in bulk : ", err);
            toast.error(err.message || 'Error in bulk delete!');
        }
    }

    const handleDeleteByFilter = async (filters = formik.values) => {
        try {
            if (!window.confirm("Are you sure you want to remove emails in bulk?")) {
                return;
            }

            if (isDeleteByFilterDisabled) return;

            setIsDeleteByFilterDisabled(true); // Disable immediately

            const { type, startDate, endDate } = filters;
            const user = JSON.parse(localStorage.getItem("user"));
            const userId = user?.data?.id;
            const userName = user?.data?.name;

            if (!userId) {
                toast.error("userId not found. please re-login and try again.");
                setIsDeleteByFilterDisabled(false);
                return;
            }

            if (!type && !startDate && !endDate) {
                toast.error("Please Provide at least one filter.");
                setIsDeleteByFilterDisabled(false);
                return;
            }

            if ((startDate && !endDate) || (endDate && !startDate)) {
                toast.error("start date and end date is required if deleting by range.");
                setIsDeleteByFilterDisabled(false);
                return;
            }

            const payload = { type, startDate, endDate, userId, userName };

            const message = await dispatch(deleteSuppressionEmailsByFilter(payload)).unwrap();
            toast.success(message || "Bulk delete initiated successfully");
            handleOpenBulkDeleteReport();

        } catch (err) {
            console.log("Error in bulk delete by filter: ", err);
            toast.error(err || "Error in bulk delete by filter!");
        } finally {
            // Disable button for 5 seconds
            setTimeout(() => {
                setIsDeleteByFilterDisabled(false);
            }, 5000);
        }
    };

    const handleOpenBulkDeleteReport = () => {
        navigate('bulk-suppression-delete-report');
    }

    const today = DateTime.now().toISODate();

    return (
        <Layout>
            <div>
                <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
                    <h3 className="m-0">Suppression Emails</h3>
                    <Button variant="outline-primary" onClick={handleOpenBulkDeleteReport}>
                        Bulk Delete Report
                    </Button>
                </div>

                {/* Filters */}
                <form
                    onSubmit={formik.handleSubmit}
                    className="mb-3 d-flex flex-wrap justify-content-between align-items-end gap-3"
                >
                    {/* Left section: filters */}
                    <div className="d-flex flex-wrap gap-3">
                        <div className="d-flex flex-column">
                            <label htmlFor="emailFilter" className="form-label">
                                Email
                            </label>
                            <input
                                id="emailFilter"
                                name="email"
                                type="text"
                                className="form-control"
                                placeholder="Search by email"
                                onChange={formik.handleChange}
                                value={formik.values.email}
                            />
                        </div>

                        <div className="d-flex flex-column">
                            <label htmlFor="typeFilter" className="form-label">
                                Type
                            </label>
                            <select
                                id="typeFilter"
                                name="type"
                                className="form-control"
                                onChange={formik.handleChange}
                                value={formik.values.type}
                            >
                                <option value="">Select Type</option>
                                <option value="Bounce">Bounce</option>
                                <option value="Complaint">Complaint</option>
                            </select>
                        </div>

                        <div className="d-flex flex-column">
                            <label htmlFor="startDateFilter" className="form-label">
                                Start Date
                            </label>
                            <input
                                id="startDateFilter"
                                name="startDate"
                                type="date"
                                className="form-control"
                                onChange={formik.handleChange}
                                value={formik.values.startDate}
                                max={today}
                            />
                        </div>

                        <div className="d-flex flex-column">
                            <label htmlFor="endDateFilter" className="form-label">
                                End Date
                            </label>
                            <input
                                id="endDateFilter"
                                name="endDate"
                                type="date"
                                className="form-control"
                                onChange={formik.handleChange}
                                value={formik.values.endDate}
                                max={today}
                            />
                        </div>

                        <div className="d-flex gap-2 align-items-end">
                            <Button type="submit" variant="primary">
                                Filter
                            </Button>
                            <Button
                                type="button"
                                variant="outline-secondary"
                                onClick={handleReset}
                            >
                                Reset
                            </Button>
                        </div>
                    </div>

                    {/* Right section: delete selected */}
                    {checkedItems.length > 0 && hasPermission(PERMISSIONS.EMAIL_SUPPRESSION_LIST.DELETE) ? (
                        <div className="align-self-end">
                            <Button variant="danger" onClick={handleDeleteInBulk}>
                                Delete Selected
                            </Button>
                        </div>
                    ) : (hasPermission(PERMISSIONS.EMAIL_SUPPRESSION_LIST.DELETE) &&
                        <Button
                            variant="danger"
                            disabled={isDeleteByFilterDisabled}
                            onClick={() => handleDeleteByFilter(formik.values)}
                        >
                            {isDeleteByFilterDisabled ? "Please wait..." : "Delete By Filter"}
                        </Button>
                    )}
                </form>

                <Table striped bordered hover responsive>
                    <thead className="text-center align-middle">
                        <tr>
                            <th>S.No.</th>
                            <th>Email</th>
                            <th>Type</th>
                            <th>Created Date</th>
                            <th>Action</th>
                            {
                                hasPermission(PERMISSIONS.EMAIL_SUPPRESSION_LIST.DELETE) && <th style={{ textAlign: 'center' }}>
                                    <div className="d-flex flex-column align-items-center gap-1">
                                        <span>Select All</span>
                                        <input
                                            type="checkbox"
                                            style={{ height: '18px', width: '18px' }}
                                            checked={isAllSelected()}
                                            onChange={handleSelectAll}
                                        />
                                    </div>
                                </th>
                            }
                        </tr>
                    </thead>

                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={6} className="text-center py-4">
                                    <Spinner animation="border" variant="primary" />
                                    <div className="mt-2">Loading...</div>
                                </td>
                            </tr>
                        ) : currentPageData.length > 0 ? (
                            currentPageData.map((record, index) => (
                                <tr key={record._id} className="align-middle text-center">
                                    <td>{(page - 1) * recordsPerPage + index + 1}</td>
                                    <td className="text-start">
                                        {record?.email?.length ? record.email.join(", ") : "NA"}
                                    </td>
                                    <td className="fw-bold text-danger">
                                        {record?.type || "NA"}
                                    </td>
                                    <td>
                                        {record?.createdAt || record?.timestamp
                                            ? DateTime.fromISO(record?.createdAt || record?.timestamp).toFormat(
                                                "yyyy-MM-dd HH:mm:ss"
                                            )
                                            : "NA"}
                                    </td>
                                    <td>
                                        <div className="d-flex justify-content-center gap-2">
                                            {
                                                hasPermission(PERMISSIONS.EMAIL_SUPPRESSION_LIST.DELETE) && <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => handleDelete(record._id, record.email)}
                                                >
                                                    Delete
                                                </Button>
                                            }
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => showPayload(record)}
                                            >
                                                View
                                            </Button>
                                        </div>
                                    </td>
                                    {
                                        hasPermission(PERMISSIONS.EMAIL_SUPPRESSION_LIST.DELETE) && <td className="text-center">
                                            <input
                                                type="checkbox"
                                                style={{ height: '18px', width: '18px' }}
                                                checked={isSelectedRow(record._id)}
                                                onChange={() => handleSelect(record)}
                                            />
                                        </td>
                                    }
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={6} className="text-center py-4">
                                    No data available
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>


                {totalPages > 1 && (
                    <SmartPagination
                        totalPages={totalPages}
                        currentPage={page}
                        onPageChange={setPage}
                    />
                )}

                <JsonModal
                    show={showModel}
                    onClose={closeModal}
                    payload={selectedPayload}
                />
            </div>
        </Layout>
    );
};

export default EmailSuppressionList;
